import { useState, useRef, useEffect } from 'react';
import { useMutation } from 'convex/react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../convex/_generated/api';
import './ProjectActionsMenu.css';

function ProjectActionsMenu({
    projectId,
    projectTitle,
    isPinned,
    onActionMessage,
    onActionError,
    onDeleteSuccess
}) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [renaming, setRenaming] = useState(false);
    const [newTitle, setNewTitle] = useState(projectTitle);
    const [confirmingArchive, setConfirmingArchive] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const menuRef = useRef(null);
    const inputRef = useRef(null);

    const updateMetadata = useMutation(api.projects.updateMetadata);
    const archiveProject = useMutation(api.projects.archiveProject);
    const togglePinned = useMutation(api.projects.togglePinned);

    // Close when clicking outside
    useEffect(() => {
        if (!open && !renaming) return;
        const handler = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setOpen(false);
                setRenaming(false);
                setConfirmingArchive(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open, renaming]);

    // Focus input when renaming
    useEffect(() => {
        if (renaming && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [renaming]);

    // Sync title prop if it changes externally
    useEffect(() => {
        if (!renaming) {
            setNewTitle(projectTitle);
        }
    }, [projectTitle, renaming]);

    const stopAnd = (event, fn) => {
        event.stopPropagation();
        fn();
    };

    const handleRenameSubmit = async (event) => {
        event?.stopPropagation?.();
        const trimmed = newTitle.trim();
        if (!trimmed || trimmed === projectTitle) {
            setRenaming(false);
            return;
        }

        try {
            setIsBusy(true);
            await updateMetadata({ projectId, title: trimmed });
            onActionMessage?.(t('projects.renamedTo', { title: trimmed }));
            setRenaming(false);
            setOpen(false);
        } catch (error) {
            onActionError?.(error.message || t('projects.failedToRenameProject'));
        } finally {
            setIsBusy(false);
        }
    };

    const handleTogglePin = async (event) => {
        event.stopPropagation();
        try {
            setIsBusy(true);
            const result = await togglePinned({ projectId });
            onActionMessage?.(result.pinned ? t('projects.projectPinnedShort') : t('projects.projectUnpinnedShort'));
            setOpen(false);
        } catch (error) {
            onActionError?.(error.message || t('projects.failedToUpdatePin'));
        } finally {
            setIsBusy(false);
        }
    };

    const handleArchive = async (event) => {
        event.stopPropagation();
        if (!confirmingArchive) {
            setConfirmingArchive(true);
            return;
        }

        try {
            setIsBusy(true);
            await archiveProject({ projectId });
            onDeleteSuccess?.();
            // No need to setOpen(false) as we likely navigate away
        } catch (error) {
            onActionError?.(error.message || t('projects.failedToArchiveProject'));
            setIsBusy(false);
            setOpen(false);
        }
    };

    if (renaming) {
        return (
            <div className="pam-rename-wrapper" ref={menuRef} onClick={(e) => e.stopPropagation()}>
                <input
                    ref={inputRef}
                    className="pam-rename-input"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    onKeyDown={(e) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') handleRenameSubmit(e);
                        if (e.key === 'Escape') setRenaming(false);
                    }}
                    onBlur={handleRenameSubmit}
                />
            </div>
        );
    }

    return (
        <div className="pam-wrapper" ref={menuRef}>
            <button
                className={`pam-trigger ${open ? 'active' : ''}`}
                onClick={(e) => stopAnd(e, () => {
                    setOpen(!open);
                    setConfirmingArchive(false);
                })}
                title={t('projects.projectOptions')}
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="1" />
                    <circle cx="12" cy="5" r="1" />
                    <circle cx="12" cy="19" r="1" />
                </svg>
            </button>

            {open && (
                <div className="pam-dropdown">
                    <button
                        className="pam-item"
                        onClick={(e) => stopAnd(e, () => {
                            setRenaming(true);
                            setOpen(false);
                        })}
                    >
                        <span className="pam-icon">✏️</span>
                        {t('common.rename')}
                    </button>

                    <button
                        className="pam-item"
                        onClick={handleTogglePin}
                        disabled={isBusy}
                    >
                        <span className="pam-icon">{isPinned ? '📍' : '📌'}</span>
                        {isPinned ? t('projects.unpinProjectFull') : t('projects.pinProject')}
                    </button>

                    <div className="pam-divider" />

                    <button
                        className={`pam-item ${confirmingArchive ? 'danger' : ''}`}
                        onClick={handleArchive}
                        disabled={isBusy}
                    >
                        <span className="pam-icon">🗑️</span>
                        {confirmingArchive ? t('projects.confirmDeleteQuestion') : t('projects.deleteProjectFull')}
                    </button>
                </div>
            )}
        </div>
    );
}

export default ProjectActionsMenu;
